import {useQuery} from "@tanstack/react-query";
import {useEffect, useRef, useState} from "react";
import {Link, Navigate} from "react-router-dom";
import {listSubscriptionPayments} from "@/api/billing";
import {useCabinetData} from "@/hooks/useCabinetData";
import {formatDate, paymentHistoryBadge, paymentHistoryTitle} from "@/lib/cabinet";

export function CabinetOperationHistoryPage() {
    const {subscription, paid, isLoading, isError, refetch} = useCabinetData();
    const historyQuery = useQuery({
        queryKey: ["cabinet", "payments", "history"],
        queryFn: () => listSubscriptionPayments(50),
        enabled: paid,
    });
    const [copiedOperationId, setCopiedOperationId] = useState<string | null>(null);
    const copyResetTimer = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (copyResetTimer.current !== null) {
                window.clearTimeout(copyResetTimer.current);
            }
        };
    }, []);

    const copyOperationId = (operationId: string) => {
        void navigator.clipboard?.writeText(operationId);
        setCopiedOperationId(operationId);
        if (copyResetTimer.current !== null) {
            window.clearTimeout(copyResetTimer.current);
        }
        copyResetTimer.current = window.setTimeout(() => {
            setCopiedOperationId((current) => current === operationId ? null : current);
            copyResetTimer.current = null;
        }, 1600);
    };

    if (isLoading || historyQuery.isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Загружаем историю</h2>
                <p>Получаем последние операции по подписке.</p>
            </div>
        );
    }

    if (isError || historyQuery.isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить историю</h2>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => void Promise.all([refetch(), historyQuery.refetch()])}
                >
                    Повторить
                </button>
            </div>
        );
    }

    if (!paid || !subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    const payments = historyQuery.data?.payments ?? [];

    return (
        <>
            <div className="cabinet-panel-head cabinet-history-head">
                <div>
                    <h2>История операций</h2>
                    <p className="cabinet-muted">Последние платежи и изменения способов оплаты.</p>
                </div>
                <Link className="btn btn-muted" to="/cabinet">Назад к возможностям</Link>
            </div>

            <article className="cabinet-panel cabinet-history-panel">
                {payments.length === 0 ? (
                    <div className="cabinet-history-empty">
                        <span aria-hidden="true">↻</span>
                        <h3>Операций пока нет</h3>
                        <p>Здесь появятся оплаты, продления и привязки способов оплаты.</p>
                    </div>
                ) : (
                    <div className="cabinet-activity">
                        {payments.map((item) => (
                            <div key={item.id} className="cabinet-activity-row">
                                <span>
                                    {formatDate(item.created_at, {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    }) ?? "—"}
                                </span>
                                <span className="cabinet-activity-main">
                                    <strong>{paymentHistoryTitle(item)}</strong>
                                    <small className="cabinet-operation-id">
                                        <span>ID: {item.id}</span>
                                        <button
                                            type="button"
                                            className={[
                                                "cabinet-copy-id",
                                                copiedOperationId === item.id ? "is-copied" : "",
                                            ].filter(Boolean).join(" ")}
                                            title={
                                                copiedOperationId === item.id
                                                    ? "ID операции скопирован"
                                                    : "Скопировать ID операции"
                                            }
                                            onClick={() => copyOperationId(item.id)}
                                        >
                                            {copiedOperationId === item.id ? "Скопировано!" : "Скопировать"}
                                        </button>
                                    </small>
                                </span>
                                <span className="cabinet-badge">{paymentHistoryBadge(item)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </article>
        </>
    );
}
